import type { AuthResponse } from "@types";
import {
  hasObjectType,
  isFunctionValue,
  isStringValue,
} from "@/utils/typePredicates";
import { readSafeStorage } from "../utils/safeStorage";

// SD-11710 · push half of the softphone extension's session handshake. The
// extension (apps/phone-extension) declares this app origin under
// `externally_connectable`, so this page may call
// chrome.runtime.sendMessage(EXTENSION_ID, …) directly; the extension's
// background stores the session and mints Telnyx seat tokens with it. The
// extension's pull bridge (a content script reading localStorage) remains the
// fallback for sessions/app builds that predate this push.
//
// Feature-detected on BOTH `VITE_PHONE_EXTENSION_ID` (unset by default →
// zero behavior change) and the page-scoped `window.chrome.runtime` Chrome
// exposes only when an installed extension is externally connectable to this
// origin. Fire-and-forget: an uninstalled/asleep extension must never affect
// login or token refresh.

/** Payload shape the extension expects (apps/phone-extension SessionSync). */
interface ExtensionSessionSync {
  accessToken: string;
  expiration: number;
  accountId: string | null;
  appOrigin: string;
  userId: string | null;
  companyId: string | null;
}

type ExtensionSessionPushMessage =
  | { type: "auth/session-clear"; appOrigin: string }
  | { type: "auth/session-sync"; session: ExtensionSessionSync };

// Narrow local shape instead of @types/chrome: the app is not an extension
// context, and the full chrome namespace would leak into every module.
interface ChromeLike {
  runtime?: {
    sendMessage?: (
      extensionId: string,
      message: ExtensionSessionPushMessage,
    ) => Promise<void>;
  };
}

/** Tell the extension the session is GONE. The pull bridge only notices a
 *  same-tab logout on its 60s poll (no storage event fires in the writing
 *  tab), so without this push the extension keeps a live JWT and Telnyx
 *  registration for up to a minute after sign-out (review, #2297). */
export const pushSessionClearToExtension = (): void => {
  if (globalThis.window === undefined) return;
  const extensionId = import.meta.env.VITE_PHONE_EXTENSION_ID;
  if (!extensionId) return;
  const runtime = (window as Window & { chrome?: ChromeLike }).chrome?.runtime;
  if (!isFunctionValue(runtime?.sendMessage)) return;
  try {
    void runtime
      .sendMessage(extensionId, {
        type: "auth/session-clear",
        appOrigin: window.location.origin,
      })
      ?.catch?.(() => undefined);
  } catch {
    // Non-Chrome browsers exposing a partial chrome shim — ignore.
  }
};

/** Re-push the CURRENT stored session (all fields live in localStorage) —
 *  used when only part of it changed (company switch), where no AuthResponse
 *  is at hand and the pull bridge's token-keyed change detection would skip
 *  the update (review, #2297). */
export const pushStoredSessionToExtension = (): void => {
  if (globalThis.window === undefined) return;
  const accessToken = readSafeStorage("accessToken");
  const expiration = Number(readSafeStorage("expiration") || "");
  if (!accessToken || !expiration) return;
  let user: { accountId?: string; id?: string } | undefined;
  try {
    const parsed: unknown = JSON.parse(readSafeStorage("user") ?? "{}");
    if (parsed && hasObjectType(parsed)) {
      user = parsed;
    }
  } catch {
    // Malformed — push the token anyway; ids stay null.
  }
  pushRawSession({
    accessToken,
    expiration,
    accountId: isStringValue(user?.accountId) ? user.accountId : null,
    userId: isStringValue(user?.id) ? user.id : null,
    companyId: readSafeStorage("companyId"),
    appOrigin: window.location.origin,
  });
};

const pushRawSession = (session: ExtensionSessionSync): void => {
  const extensionId = import.meta.env.VITE_PHONE_EXTENSION_ID;
  if (!extensionId) return;
  const runtime = (window as Window & { chrome?: ChromeLike }).chrome?.runtime;
  if (!isFunctionValue(runtime?.sendMessage)) return;
  try {
    void runtime
      .sendMessage(extensionId, { type: "auth/session-sync", session })
      ?.catch?.(() => undefined);
  } catch {
    // Non-Chrome browsers exposing a partial chrome shim — ignore.
  }
};

export const pushSessionToExtension = (auth: AuthResponse): void => {
  if (globalThis.window === undefined) return;
  const extensionId = import.meta.env.VITE_PHONE_EXTENSION_ID;
  if (!extensionId) return;
  const runtime = (window as Window & { chrome?: ChromeLike }).chrome?.runtime;
  if (!isFunctionValue(runtime?.sendMessage)) return;
  const session: ExtensionSessionSync = {
    accessToken: auth.accessToken,
    expiration: auth.expiration,
    accountId: auth.user.accountId || null,
    userId: auth.user.id || null,
    // Same source the extension's pull bridge reads; session lifecycle
    // persistence writes it before pushing, so the two paths can't disagree.
    companyId: readSafeStorage("companyId"),
    appOrigin: window.location.origin,
  };
  try {
    void runtime
      .sendMessage(extensionId, { type: "auth/session-sync", session })
      // Not installed / service worker asleep — silently fine.
      ?.catch?.(() => undefined);
  } catch {
    // Non-Chrome browsers exposing a partial chrome shim — ignore.
  }
};
